import type { AssessmentPage, AssessmentRequirement, AssuranceLevel } from '../api/dnscAssessment'
import type { DnscEvidence, DnscRequirement } from '../api/overview'
import { isKeyAt, onForm } from './assessment'

/**
 * Fila "Summary" din /overview/nis2, fara React: fiecare cerinta de pe formularul
 * nivelului intr-o singura grupa - o ai, o reparam noi, o completati voi.
 */

export type SummaryGroup = 'have' | 'fix' | 'fill'
export type MissingSide = 'documentation' | 'implementation' | 'documentation and implementation'

export interface SummaryRow {
  requirement: AssessmentRequirement
  /** Masura cheie la nivelul firmei. */
  key: boolean
  group: SummaryGroup
  na: boolean
  /** Nota de documentatie: numai din raspunsul clientului. */
  doc: number | null
  /** Implementarea efectiva: raspunsul clientului, altfel propunerea platformei. */
  impl: number | null
  implFrom: 'answer' | 'proposal' | null
  docMissing: boolean
  /** Ce lipseste; null pe "have". */
  missing: MissingSide | null
  /** Dovezile picate (verdict `fail`) - numai pe "we fix". */
  failing: DnscEvidence[]
  /** Dovezile cu nota din care vine implementarea propusa; gol cand nota e a clientului. */
  measured: DnscEvidence[]
  /** Ce nu vede platforma (`Dnsc::requirements` -> `still`). */
  still: string
}

export interface SummaryCounts {
  total: number
  have: number
  fix: number
  fill: number
  missing: number
}

export interface SummaryData {
  level: AssuranceLevel
  target: number
  counts: SummaryCounts
  keyCounts: SummaryCounts
  have: SummaryRow[]
  fix: SummaryRow[]
  fill: SummaryRow[]
}

function below(n: number | null, target: number): boolean {
  return n === null || n < target
}

function missingOf(doc: boolean, impl: boolean): MissingSide | null {
  if (doc && impl) return 'documentation and implementation'
  if (doc) return 'documentation'
  if (impl) return 'implementation'
  return null
}

function rowOf(r: AssessmentRequirement, level: AssuranceLevel, target: number, proposal: DnscRequirement | undefined): SummaryRow {
  const a = r.answer
  const key = isKeyAt(r, level)
  const still = proposal?.still ?? ''
  if (a?.not_applicable) {
    return { requirement: r, key, group: 'have', na: true, doc: null, impl: null, implFrom: null, docMissing: false, missing: null, failing: [], measured: [], still }
  }

  const doc = a?.doc_score ?? null
  const answered = a?.impl_score ?? null
  const proposed = proposal?.score ?? null
  const impl = answered ?? proposed
  const implFrom = answered !== null ? 'answer' : proposed !== null ? 'proposal' : null
  const docMissing = below(doc, target)
  const implMissing = below(impl, target)
  const failing = (proposal?.evidence ?? []).filter((e) => e.verdict === 'fail')

  let group: SummaryGroup = 'fill'
  if (!docMissing && !implMissing) group = 'have'
  else if (implMissing && proposed !== null && proposed < target && failing.length > 0) group = 'fix'

  return {
    requirement: r,
    key,
    group,
    na: false,
    doc,
    impl,
    implFrom,
    docMissing,
    missing: group === 'have' ? null : missingOf(docMissing, implMissing),
    failing: group === 'fix' ? failing : [],
    measured: implFrom === 'proposal' ? (proposal?.evidence ?? []).filter((e) => e.score !== null) : [],
    still,
  }
}

function countsOf(rows: SummaryRow[]): SummaryCounts {
  const have = rows.filter((r) => r.group === 'have').length
  const fix = rows.filter((r) => r.group === 'fix').length
  const fill = rows.filter((r) => r.group === 'fill').length
  return { total: rows.length, have, fix, fill, missing: fix + fill }
}

/**
 * Grupele, din cele doua cereri pe care ecranul le are deja. Null fara nivel
 * de asigurare sau fara regulile lui. Tinta = tinta masurilor cheie, rotunjita in sus.
 */
export function summarizeNis2(page: AssessmentPage, proposals: Map<string, DnscRequirement>): SummaryData | null {
  const level = page.level?.level ?? null
  const rules = level ? page.levels?.[level] : undefined
  if (!level || !rules) return null

  const target = Math.ceil(rules.target_key)
  const rows = (page.requirements ?? []).filter((r) => onForm(r, level)).map((r) => rowOf(r, level, target, proposals.get(r.code)))
  /* Masurile cheie primele; altfel ordinea catalogului (sort e stabil). */
  const ordered = [...rows].sort((x, y) => Number(y.key) - Number(x.key))

  return {
    level,
    target,
    counts: countsOf(rows),
    keyCounts: countsOf(rows.filter((r) => r.key)),
    have: ordered.filter((r) => r.group === 'have'),
    fix: ordered.filter((r) => r.group === 'fix'),
    fill: ordered.filter((r) => r.group === 'fill'),
  }
}

/* --- filtrele filei si vederea "By fix" --------------------------------- */

export type GroupFilter = 'all' | SummaryGroup

export interface SummaryFilters {
  group: GroupFilter
  keyOnly: boolean
  search: string
}

export const NO_SUMMARY_FILTERS: SummaryFilters = { group: 'all', keyOnly: false, search: '' }

export interface SummaryView {
  have: SummaryRow[]
  fix: SummaryRow[]
  fill: SummaryRow[]
  shown: number
  total: number
  /** Pe grupe, dupa "key only" si cautare, inainte de filtrul de grupa (cifrele de pe butoane). */
  groupCounts: Record<GroupFilter, number>
}

/** Fara majuscule si fara diacritice: textele DNSC sunt in romana (ă, ț, ș cu virgula sau cedila). */
export function fold(text: string): string {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
}

function termsOf(search: string): string[] {
  return fold(search).split(/\s+/).filter((t) => t !== '')
}

function evidenceWords(e: DnscEvidence): string[] {
  return [e.title, e.fix ?? '', ...e.sample]
}

/* Codul, textul, ce lipseste, si dovezile randului: titlu, ce e de facut, masini / conturi. */
function rowWords(row: SummaryRow): string[] {
  return [row.requirement.code, row.requirement.text, row.missing ?? '', ...[...row.failing, ...row.measured].flatMap(evidenceWords)]
}

function matches(words: string[], terms: string[]): boolean {
  if (terms.length === 0) return true
  const text = fold(words.join('\n'))
  return terms.every((t) => text.includes(t))
}

export function filterSummary(data: SummaryData, f: SummaryFilters): SummaryView {
  const terms = termsOf(f.search)
  const keep = (rows: SummaryRow[]) => rows.filter((r) => (!f.keyOnly || r.key) && matches(rowWords(r), terms))
  const have = keep(data.have)
  const fix = keep(data.fix)
  const fill = keep(data.fill)
  const groupCounts = { all: have.length + fix.length + fill.length, have: have.length, fix: fix.length, fill: fill.length }
  const only = (g: SummaryGroup, rows: SummaryRow[]) => (f.group === 'all' || f.group === g ? rows : [])
  const view = { have: only('have', have), fix: only('fix', fix), fill: only('fill', fill) }
  return { ...view, shown: view.have.length + view.fix.length + view.fill.length, total: data.counts.total, groupCounts }
}

/** Un control picat, o singura data, cu cerintele "we fix" in care apare. */
export interface FixControl {
  key: string
  title: string
  question: string
  figure: string
  fix: string
  url: string
  /** Masinile / conturile, reunite din toate cerintele. */
  sample: string[]
  /** Toate cerintele "we fix" in care pica; `shown` = trece de filtrele de acum. */
  requirements: Array<{ code: string; key: boolean; shown: boolean }>
  keyCount: number
}

/**
 * Vederea "By fix": fiecare control picat din `all` (randurile "we fix"), o data.
 * Numaratoarea e pe toate; filtrele aleg numai CE controale se arata: cele care
 * pica intr-un rand din `shown` si pe care cautarea le gaseste (in cuvintele lor
 * sau ale cerintelor aratate). Ordinea: masuri cheie, cerinte, titlu.
 */
export function byFix(all: SummaryRow[], shown: SummaryRow[] = all, search = ''): FixControl[] {
  const terms = termsOf(search)
  const visible = new Set(shown.map((r) => r.requirement.code))
  const out = new Map<string, FixControl & { words: string[] }>()
  for (const row of all) {
    for (const e of row.failing) {
      if (e.verdict !== 'fail') continue
      let c = out.get(e.key)
      if (!c) {
        c = { key: e.key, title: e.title, question: e.question ?? '', figure: e.figure, fix: e.fix ?? '', url: e.url ?? '', sample: [], requirements: [], keyCount: 0, words: [] }
        out.set(e.key, c)
      }
      for (const name of e.sample) if (!c.sample.includes(name)) c.sample.push(name)
      if (c.requirements.some((r) => r.code === row.requirement.code)) continue
      const isShown = visible.has(row.requirement.code)
      c.requirements.push({ code: row.requirement.code, key: row.key, shown: isShown })
      if (row.key) c.keyCount++
      if (isShown) c.words.push(row.requirement.code, row.requirement.text, row.missing ?? '')
    }
  }
  return [...out.values()]
    .filter((c) => c.requirements.some((r) => r.shown) && matches([c.title, c.fix, ...c.sample, ...c.words], terms))
    .sort((a, b) => b.keyCount - a.keyCount || b.requirements.length - a.requirements.length || a.title.localeCompare(b.title))
    .map(({ words: _words, ...c }) => c)
}
